import {gql} from 'graphql-request';
import {useSetAtom} from 'jotai';
import {useEffect} from 'react';
import {useGqlRequest} from '@/api/request';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {FranchiseItem} from '../@types';
import {starredBrandsInfoAtom} from '../atom';
import {FRANCHISE_DETAIL_FIELDS} from './franchise';

export const useStarredBrands = () => {
  const request = useGqlRequest({
    query: QUERY_STARRED_BRANDS,
  });
  const setSelected = useSetAtom(starredBrandsInfoAtom);

  const queryFn = (
    context: QueryFunctionContext
  ):
    | undefined
    | Promise<{
        result?: FranchiseItem[];
      }> => {
    return request(context);
  };

  const updateSelectedStarredBrands = (starredList: FranchiseItem[]) => {
    const starredIds = starredList.map((i) => i.id);

    setSelected((prev) => ({
      ...prev,
      selected: prev.selected.filter((i) => {
        return starredIds.findIndex((j) => j === i) !== -1;
      }),
    }));
  };

  const select = (data?: {result?: FranchiseItem[]}) => {
    return data?.result;
  };

  const query = useQuery({
    queryKey: ['map', 'starred-brands'],
    queryFn,
    select,
  });

  useEffect(() => {
    if (query?.data) {
      updateSelectedStarredBrands(query.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.data]);

  return query;
};

export const useAddStarredBrands = () => {
  const request = useGqlRequest({
    query: ADD_STARRED_BRANDS,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['map', 'starred-brands', 'add'],
    mutationFn: (brands: string[]) => request({brands}),
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: ['map', 'starred-brands']}),
  });
};

export const useRemoveStarredBrands = () => {
  const request = useGqlRequest({
    query: REMOVE_STARRED_BRANDS,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['map', 'starred-brands', 'remove'],
    mutationFn: (brands: string[]) => request({brands}),
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: ['map', 'starred-brands']}),
  });
};

const QUERY_STARRED_BRANDS = gql`
  query listFavoriteBrands {
    result: listFavoriteBrands {
      ${FRANCHISE_DETAIL_FIELDS}
    }
  }
`;

const ADD_STARRED_BRANDS = gql`
  mutation insertFavoriteBrands($brands: [String!]!) {
    result: insertFavoriteBrands(brands: $brands) {
      ${FRANCHISE_DETAIL_FIELDS}
    }
  }
`;

const REMOVE_STARRED_BRANDS = gql`
  mutation removeFavoriteBrands($brands: [String!]!) {
    result: removeFavoriteBrands(brands: $brands) {
      ${FRANCHISE_DETAIL_FIELDS}
    }
  }
`;
